// 通道组合表单里策略与各参数的说明（docs/10 第 3.1、4、5、9 节，决策 D44、D69、D74、D92、D96、D97），显示在标签右侧的问号里。
// 字段下方的提示只写范围与默认值，含义、例子与限制写在这里。

/** 策略下拉里每项的一句话说明。 */
export const STRATEGY_BRIEF: Record<string, string> = {
  priority: "按优先级取第一个可用的成员",
  weighted: "按权重比例分流",
  least_cost: "取每段成本最低的通道",
  quality_based: "按送达率、成本、延迟打分分流",
  number_affinity: "按目的号码在各通道的历史调整顺序",
  primary_backup: "固定走主通道，送达率明显变差时切到备用",
  time: "按北京时间的时段选成员",
  retry: "首发失败或超时后换重试成员再发",
};

/** 策略标签问号里的完整说明：怎么选、成员限制、适合的场景。 */
export const STRATEGY_HELP: Record<string, string> = {
  priority: "按成员的优先级从小到大，取第一个可用的成员。熔断中的成员让给健康的成员，全都在熔断中才在它们里面选。成员可以是通道或通道组合。适合固定主用、其余兜底。",
  weighted: "在可用的成员里按权重比例抽签：权重是相对值，例如 3 与 1 大约分到 75% 与 25%；同一条消息每次抽签结果相同，可以回放。熔断中的成员让给健康的成员。成员可以是通道或通道组合。",
  least_cost: "按每段成本（换算成基准币种）从低到高排，取最便宜的可用通道；没配成本的排最后，同价按优先级。不按售价过滤，价格倒挂由利润保护兜底。成员只能是通道。",
  quality_based: "给每条通道打分：质量（近 1 小时送达率，按账户修正）、成本优势（越便宜越高）、延迟优势（回执越快越高）按客户档位的权重加权，再按分数的平方加权抽签，分数接近时流量分到几条通道。样本不足 50 的通道取其他通道的均值。成员只能是通道。",
  number_affinity: "先按优先级排，再看目的号码的历史：近期在某条通道送达过就把它提到最前，在某条通道连续失败到门槛就把它挪到最后（不剔除）。只看真实回执；没有记录的号码与静态优先级相同。成员只能是通道。",
  primary_backup: "优先级最小的是主通道，其余按优先级是备用。平时走主通道，其余通道分一点小流量持续测送达率；主通道的送达率比某个备用低出切走阈值时整体切到那个备用，恢复后切回。状态按运营商每分钟判定一次，通道组合列表的状态列标出切走或验证中的运营商，点名称进详情看各运营商的当前通道。成员只能是支持回执的通道，至少两个。",
  time: "取时段包含当前时刻的那个成员。时段按北京时间，结束早于开始表示跨午夜；各段不重叠且覆盖全天。当前时段的成员不可用时发送失败，不会改用别的时段的成员。",
  retry: "先发首发成员；尝试开始后超时没收到送达成功的回执、或已确定失败，就发给重试成员里还没试过的通道，直到次数用完。超时后旧的尝试照常等回执，迟到的送达也算，可能重复送达。嵌套时只有最外层的失败重试组合计时计次。",
};

/** 成员下拉的说明：哪些能当成员。 */
export function memberHelp(strategy: string): string {
  if (strategy === "primary_backup") return "只能选支持回执的通道：不支持回执的通道没有送达率样本，没法比较。";
  if (["least_cost", "quality_based", "number_affinity"].includes(strategy)) return "只能选通道：这个策略要比较各通道的成本、送达率或号码历史。";
  return "可以选通道，也可以选另一个通道组合（最多嵌套 3 层）；选中组合时按它自己的策略再往下选。";
}

/** 成员优先级的说明，各策略里优先级的作用不同。 */
export function priorityHelp(strategy: string): string {
  switch (strategy) {
    case "least_cost": return "成本相同时按优先级，越小越靠前。";
    case "quality_based": return "首发按分数的平方加权抽签；其余同分候选按优先级排列，越小越靠前。打分失败时回退到优先级选路。";
    case "number_affinity": return "没有号码记录时的顺序，越小越靠前；有记录时在这个顺序上提前或挪后。";
    case "primary_backup": return "最小的是主通道，其余按从小到大是备用顺序；不能重复。";
    default: return "越小越先选。";
  }
}

/** 其余参数的说明，键与草稿的字段名一致。 */
export const PARAM_HELP = {
  weight: "相对值，按比例分流，例如 3 与 1 大约分到 75% 与 25%。0 表示平时不分流量。",
  time: "北京时间，含开始不含结束，例如 08:00–20:00 与 20:00–08:00 两段正好覆盖全天；结束早于开始表示跨午夜。",
  first: "消息先发给它。它不可用，或只能从熔断中的通道里降级选出而重试成员有健康的通道时，首发直接发给重试成员。可以是通道或通道组合。",
  retry: "首发失败或超时后发给它；是通道组合时按它自己的策略选，并排除这条消息已经试过的通道。不能是、也不能包含失败重试的通道组合。",
  timeout_sec: "每次尝试开始后，这么多秒内没收到送达成功的回执就换通道重试。提交失败、收到失败回执这类已确定送不到的，不等超时立即重试。不支持回执的通道提交成功即算送达，不会因超时重试。",
  max_attempts: "一条消息最多发几次，含首发。次数用完，或重试成员里没有还没试过的通道时，消息按最后一次的失败原因结束。",
  recent_days: "这么多天内，目的号码在某条通道上送达过，就把最近送达的那条通道提到最前。只记真实的送达回执：不支持回执的通道、提交阶段的失败都不记。",
  fail_threshold: "目的号码在某条通道上连续失败达到这么多次，就把这条通道挪到最后（不剔除）；在那条通道送达一次才清零。",
  threshold: "主用时按备用顺序比较近 3 分钟的送达率：某个备用比主通道高出这么多个百分点、样本够 20 条，连续 2 分钟都是它，就整体切过去。高出但样本不够 20 条时，先给它验证流量再判断。",
  recover_threshold: "切走满 10 分钟后，只看切走之后的样本（先看近 5 分钟，不够往前扩，最长 20 分钟）：当前通道比主通道高出不到这么多个百分点，就切回主通道。必须小于切走阈值，避免来回切。",
  probe_ratio: "当前通道以外的通道合占的流量，千分比，平均分给它们，让每条通道一直有新的送达率样本。50 即 5%。",
  verify_ratio: "送达率看起来更好但样本不够的通道，验证期间合占这么多流量，约 5 分钟后按新样本判断要不要切换；没通过就冷却 10 分钟。要大于小流量。",
  min_samples: "主通道（切回时还有当前通道）的样本够这么多条才判断要不要切换；样本太少时送达率波动大，容易误切。",
  params: "按客户档位设打分权重，例如 {\"weights\": {\"premium\": {\"quality\": 0.7, \"cost\": 0.2, \"latency\": 0.1}}}。quality 是送达率，cost 是成本优势，latency 是延迟优势，只接受 weights；三个权重必须是有限的非负数字，缺省项按 0，总和必须是有限的正数，不必等于 1。显式 null 不作为数字接受。没写的档位用默认权重：premium 0.7 / 0.2 / 0.1，其余 0.5 / 0.4 / 0.1。",
} as const;
